import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Switch,
  Divider
} from '@mui/material';
import { instruments } from '../../constants/instrumentation';
import { SelectedInstrument } from '../../types/instrumentTypes';
import { Performer, InstrumentWithPerformer } from '../../types/BandRecommendations';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';

interface InstrumentSelectionProps {
  value: SelectedInstrument[];
  onChange: (instruments: SelectedInstrument[]) => void;
  showPerformerNames?: boolean;
}

const InstrumentSelection = ({ value, onChange, showPerformerNames = true }: InstrumentSelectionProps) => {
  const [inputValue, setInputValue] = useState('');
  const [showEffectsFor, setShowEffectsFor] = useState<string | null>(null);

  const allOptions = instruments.flatMap(category => 
    category.options.map(instrument => ({
      ...instrument,
      category: category.label
    }))
  );

  // Auto-show effects toggle for existing instruments
  useEffect(() => {
    if (value.length > 0 && !showEffectsFor) {
      setShowEffectsFor(value[0].id);
    }
  }, [value]);

  const handleInstrumentAdd = (newValue: any) => {
    if (!newValue) return;
    const isCustom = typeof newValue === 'string';
    const name = isCustom ? newValue.trim() : newValue.name;
    if (!name) return;
    const id = isCustom ? `custom-${name.toLowerCase().replace(/\s+/g, '-')}` : newValue.id;
    const newInstrument: SelectedInstrument = {
      id,
      name,
      withEffects: false,
      performer: showPerformerNames ? { name: '' } : undefined,
    };
    onChange([newInstrument]);
    setShowEffectsFor(id);
    setInputValue('');
  };

  const handleEffectsToggle = (instrumentId: string) => {
    const updatedInstruments = value.map(instrument => 
      instrument.id === instrumentId
        ? { ...instrument, withEffects: !instrument.withEffects }
        : instrument
    );
    onChange(updatedInstruments);
  };

  const handlePerformerNameChange = (instrumentId: string, performerName: string) => {
    // FIX 1: Always store the raw value — never use .trim() as an existence check
    // mid-keystroke, as it resets the controlled input when a space is typed.
    // Trimming happens at submission time, not here.
    const updatedInstruments = value.map(instrument => 
      instrument.id === instrumentId
        ? { 
            ...instrument, 
            performer: { name: performerName }
          }
        : instrument
    );
    onChange(updatedInstruments);
  };

  const handleDelete = (instrumentId: string) => {
    const updatedInstruments = value.filter(instrument => instrument.id !== instrumentId);
    onChange(updatedInstruments);
    if (showEffectsFor === instrumentId) {
      setShowEffectsFor(null);
    }
  };

  return (
    <Box>
      {/* FIX 2: autoComplete="off" on the TextField inside Autocomplete prevents
          browser from caching performer names typed elsewhere on the page and
          bleeding them into this instrument search dropdown as suggestions. */}
      <Autocomplete
        options={allOptions}
        groupBy={(option) => option.category}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, newValue) => {
          if (newValue) {
            handleInstrumentAdd(newValue);
          }
        }}
        value={value.length > 0 ? allOptions.find(opt => opt.id === value[0].id) || null : null}
        disableClearable={false}
        freeSolo={true}
        filterSelectedOptions={false}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add Instrument"
            placeholder="Start typing to search instruments..."
            fullWidth
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
            }}
          />
        )}
      />

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {value.map((instrument) => (
          <Paper
            key={instrument.id}
            elevation={1}
            sx={{ p: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label={`${instrument.name}${instrument.withEffects ? ' (with effects)' : ''}`}
                onDelete={() => handleDelete(instrument.id)}
                color={instrument.withEffects ? "primary" : "default"}
                onClick={() => setShowEffectsFor(showEffectsFor === instrument.id ? null : instrument.id)}
                icon={instrument.performer ? <PersonIcon /> : <MusicNoteIcon />}
              />
              
              {showEffectsFor === instrument.id && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={instrument.withEffects}
                      onChange={() => handleEffectsToggle(instrument.id)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      With Effects
                    </Typography>
                  }
                />
              )}

              {showPerformerNames && (
                <input
                  type="text"
                  placeholder="Enter performer name"
                  value={instrument.performer?.name || ''}
                  onChange={(e) => handlePerformerNameChange(instrument.id, e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  style={{
                    marginLeft: '8px',
                    flexGrow: 1,
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    backgroundColor: 'transparent',
                    color: 'inherit'
                  }}
                />
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default InstrumentSelection;
